import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, IconButton } from '@mui/material';
import { Close, ArrowBack, ArrowForward } from '@mui/icons-material';
import { TourStep } from '../../contexts/TourContext';

interface SimpleTourProps {
  steps: TourStep[];
  currentStep: number;
  onNext: () => void;
  onPrevious: () => void;
  onClose: () => void;
  onComplete: () => void;
  isOpen: boolean;
}

const SimpleTour: React.FC<SimpleTourProps> = ({
  steps,
  currentStep,
  onNext,
  onPrevious,
  onClose,
  onComplete,
  isOpen,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const targetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen && steps[currentStep]) {
      const target = document.querySelector(steps[currentStep].target) as HTMLElement;
      targetRef.current = target;
      
      if (target) {
        const rect = target.getBoundingClientRect();
        setPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
        
        // Scroll to target
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [isOpen, currentStep, steps]);

  if (!isOpen || !steps[currentStep]) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <>
      {/* Overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          pointerEvents: 'auto',
        }}
        onClick={onClose}
      />
      
      {/* Highlighted area */}
      {targetRef.current && (
        <Box
          sx={{
            position: 'fixed',
            top: position.top - 4,
            left: position.left - 4,
            width: position.width + 8,
            height: position.height + 8,
            border: '3px solid #1976d2',
            borderRadius: 2,
            zIndex: 9999,
            pointerEvents: 'none',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
          }}
        />
      )}

      {/* Tooltip */}
      <Paper
        sx={{
          position: 'fixed',
          top: position.top + position.height + 10,
          left: position.left,
          width: 350,
          maxWidth: 'calc(100vw - 32px)',
          p: 2,
          zIndex: 10000,
          boxShadow: 3,
          direction: 'rtl',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          {step.title && (
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              {step.title}
            </Typography>
          )}
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          {step.content}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            مرحله {currentStep + 1} از {steps.length}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isFirstStep && (
              <Button size="small" onClick={onPrevious} startIcon={<ArrowBack />}>
                قبلی
              </Button>
            )}
            {isLastStep ? (
              <Button size="small" variant="contained" onClick={onComplete}>
                پایان
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={onNext} endIcon={<ArrowForward />}>
                بعدی
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default SimpleTour;
